import { Box, Link, Stack, Typography } from '@mui/material';

interface InformationTabProps {
    additionalData: Record<string, unknown>;
    excludedKeys?: string[];
}

const DEFAULT_EXCLUDED_KEYS: string[] = [];

export default function InformationTab({
    additionalData,
    excludedKeys = DEFAULT_EXCLUDED_KEYS,
}: Readonly<InformationTabProps>) {
    const allowedFields = ['description', 'services', 'contact_email', 'contact_phone'];

    const entries = Object.entries(additionalData);
    const filteredEntries = entries.filter(([key]) => allowedFields.includes(key) && !excludedKeys.includes(key));

    if (filteredEntries.length === 0) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography
                    sx={{
                        color: 'text.secondary',
                    }}
                    variant="body2"
                >
                    No additional information available
                </Typography>
            </Box>
        );
    }

    const getDisplayValue = (key: string, value: unknown) => {
        const stringValue = value as string;

        switch (key) {
            case 'contact_email':
                return (
                    <Link
                        href={`mailto:${stringValue}`}
                        sx={{
                            '&:hover': { textDecoration: 'underline' },
                            textDecoration: 'none',
                        }}
                    >
                        {stringValue}
                    </Link>
                );
            case 'contact_phone':
                return (
                    <Link
                        href={`tel:${stringValue}`}
                        sx={{
                            '&:hover': { textDecoration: 'underline' },
                            textDecoration: 'none',
                        }}
                    >
                        {stringValue}
                    </Link>
                );
            case 'description':
            case 'services':
                return (
                    <Typography
                        sx={{
                            color: 'text.primary',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                        }}
                        variant="body1"
                    >
                        {stringValue}
                    </Typography>
                );
            default:
                return stringValue;
        }
    };

    const formatKey = (key: string) => {
        switch (key) {
            case 'contact_email':
                return 'Email';
            case 'contact_phone':
                return 'Phone';
            case 'description':
                return 'Description';
            case 'services':
                return 'Services';
            default:
                return key
                    .replaceAll('_', ' ')
                    .replaceAll(/\bcontact\b/gi, '')
                    .replaceAll(/\binstance\b/gi, '')
                    .trim()
                    .replace(/^\w/, (c) => c.toUpperCase());
        }
    };

    return (
        <Box sx={{ px: 1 }}>
            <Stack spacing={3}>
                {filteredEntries.map(([key, value]) => {
                    const stringValue = value as string;

                    // Skip empty values
                    if (!stringValue || stringValue.length === 0) {
                        return null;
                    }

                    const formattedKey = formatKey(key);

                    return (
                        <Box key={key}>
                            <Typography
                                gutterBottom
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                }}
                                variant="body2"
                            >
                                {formattedKey}
                            </Typography>
                            <Box>{getDisplayValue(key, value)}</Box>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}
